import { useEffect } from "react";
import { Admin, Resource } from "react-admin";
import FolderIcon from "@mui/icons-material/Folder";
import LayersIcon from "@mui/icons-material/Layers";
import PeopleIcon from "@mui/icons-material/People";

import authProvider, { TOKEN_KEY } from "./authProvider";
import dataProvider from "./dataProvider";
import { queryClient } from "./queryClient";
import { aereTheme } from "./theme";
import { AereLayout } from "./components/AereLayout";

import { ProjectList } from "./resources/projects/ProjectList";
import { ProjectEdit } from "./resources/projects/ProjectEdit";
import { ProjectCreate } from "./resources/projects/ProjectCreate";

import { ModelList } from "./resources/models/ModelList";
import { ModelEdit } from "./resources/models/ModelEdit";
import { ModelCreate } from "./resources/models/ModelCreate";

import { UserList } from "./resources/users/UserList";
import { UserEdit } from "./resources/users/UserEdit";
import { UserCreate } from "./resources/users/UserCreate";

export default function App() {
  useEffect(() => {
    const viewerUrl = import.meta.env.VITE_VIEWER_URL ?? 'http://localhost:3000';
    const handler = (e: MessageEvent) => {
      if (e.origin !== new URL(viewerUrl).origin) return;
      if (e.data?.type === 'aere:logout') {
        // Set flag so authProvider.logout() skips the postMessage back to the
        // viewer, preventing a ping-pong loop.
        (window as Window & { _loggingOutFromMessage?: boolean })._loggingOutFromMessage = true;
        localStorage.removeItem(TOKEN_KEY);
        // Do NOT use window.location.href here — that causes a full reload which
        // re-runs authProvider.logout() and restarts the loop. React-admin will
        // detect the missing token on its next checkAuth and redirect to /login.
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  return (
    <Admin
      authProvider={authProvider}
      dataProvider={dataProvider}
      queryClient={queryClient}
      disableTelemetry
      title="Aere Admin"
      theme={aereTheme}
      requireAuth
      layout={AereLayout}
    >
      {(permissions) => [
        <Resource
          key="projects"
          name="projects"
          list={ProjectList}
          edit={ProjectEdit}
          create={permissions === "admin" ? ProjectCreate : undefined}
          icon={FolderIcon}
          options={{ label: "Progetti" }}
        />,
        <Resource
          key="models"
          name="models"
          list={ModelList}
          edit={ModelEdit}
          create={ModelCreate}
          icon={LayersIcon}
          options={{ label: "Modelli" }}
        />,
        permissions === "admin" ? (
          <Resource
            key="users"
            name="users"
            list={UserList}
            edit={UserEdit}
            create={UserCreate}
            icon={PeopleIcon}
            options={{ label: "Utenti" }}
          />
        ) : null,
      ]}
    </Admin>
  );
}
